import { getAuth } from 'firebase/auth';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Standard API response interface
export interface ApiResponse<_T = unknown> {
    status: 'success' | 'error';
    [key: string]: unknown;
}

// API error class for better error handling
export class ApiError extends Error {
    constructor(
        message: string,
        public status?: number,
        public response?: unknown
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

// Centralized API service class
export class ApiService {
    private baseUrl: string;

    constructor(baseUrl: string = API_BASE_URL) {
        this.baseUrl = baseUrl;
    }

    // Get Firebase auth token
    private async getAuthToken(): Promise<string | null> {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                // Force refresh token to ensure it's not expired
                const token = await user.getIdToken(true);
                console.log('Auth token obtained:', token ? 'Yes' : 'No');
                return token;
            }
            console.log('No authenticated user found');
            return null;
        } catch (error) {
            console.error('Error getting auth token:', error);
            return null;
        }
    }

    // Make authenticated API request
    protected async makeRequest<T = unknown>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        try {
            const token = await this.getAuthToken();

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(options.headers as Record<string, string>),
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
                console.log('Making request with token to:', `${this.baseUrl}${endpoint}`);
            } else {
                console.log('Making request without token to:', `${this.baseUrl}${endpoint}`);
            }

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError(
                    errorData.message || `HTTP error! status: ${response.status}`,
                    response.status,
                    errorData
                );
            }

            const data = await response.json();
            return data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            console.error(`API request failed for ${endpoint}:`, error);
            throw new ApiError(
                error instanceof Error ? error.message : 'Unknown API error'
            );
        }
    }

    // Convenience methods for different HTTP verbs
    public async get<T = unknown>(endpoint: string): Promise<T> {
        return this.makeRequest<T>(endpoint, { method: 'GET' });
    }

    public async post<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
        return this.makeRequest<T>(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    public async put<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
        return this.makeRequest<T>(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    public async delete<T = unknown>(endpoint: string): Promise<T> {
        return this.makeRequest<T>(endpoint, { method: 'DELETE' });
    }

    public async patch<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
        return this.makeRequest<T>(endpoint, {
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    // Method for downloading files (returns blob with filename)
    public async downloadFile(
        endpoint: string, 
        data?: unknown, 
        method: 'GET' | 'POST' = 'POST'
    ): Promise<{ blob: Blob; filename: string }> {
        try {
            const token = await this.getAuthToken();

            const headers: Record<string, string> = {
                ...(data ? { 'Content-Type': 'application/json' } : {}),
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method,
                headers,
                body: data ? JSON.stringify(data) : undefined,
            });

            if (!response.ok) {
                // Try to parse error as JSON first
                try {
                    const errorData = await response.json();
                    throw new ApiError(
                        errorData.error || errorData.message || `HTTP error! status: ${response.status}`,
                        response.status,
                        errorData
                    );
                } catch (jsonError) {
                    // If JSON parsing fails, use status text
                    throw new ApiError(
                        `HTTP error! status: ${response.status}`,
                        response.status
                    );
                }
            }

            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'download.docx';
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            const blob = await response.blob();
            return { blob, filename };
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            console.error(`File download failed for ${endpoint}:`, error);
            throw new ApiError(
                error instanceof Error ? error.message : 'Unknown download error'
            );
        }
    }
}

// Export a default instance for direct use
export const apiService = new ApiService(); 